if (!customElements.get('loyly-gift-options')) {
  customElements.define(
    'loyly-gift-options',
    class LoylyGiftOptions extends HTMLElement {
      connectedCallback() {
        this.giftVariantId = Number(this.dataset.giftVariantId);
        this.luxuryVariantId = Number(this.dataset.luxuryVariantId);
        this.toggle = this.querySelector('[data-gift-toggle]');
        this.status = this.querySelector('[data-gift-status]');

        this.toggle?.addEventListener('change', this.onToggle);
        this.syncComplimentaryPackaging();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('change', this.onToggle);
      }

      request = async (url, body) => {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error('Gift option could not be updated.');
        return response.json();
      };

      getCart = async () => {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Cart could not be loaded.');
        return response.json();
      };

      setBusy(isBusy, message = '') {
        if (this.toggle) this.toggle.disabled = isBusy || this.dataset.hasLuxury === 'true';
        this.classList.toggle('is-loading', isBusy);
        if (this.status) this.status.textContent = message;
      }

      addGift = async (source) => {
        await this.request(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          items: [
            {
              id: this.giftVariantId,
              quantity: 1,
              properties: { _gift_packaging_source: source },
            },
          ],
        });
      };

      removeGift = async (key) => {
        await this.request(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { id: key, quantity: 0 });
      };

      clearMessage = async () => {
        await this.request(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          attributes: { 'Gift message': '' },
        });
      };

      // Free-packaging marker recorded on the order instead of a product line.
      FREE_LABEL = 'Included free with Luxury Sheepskin Sauna Hat';

      setPackagingAttribute = async (value) => {
        await this.request(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          attributes: { 'Gift packaging': value },
        });
      };

      syncComplimentaryPackaging = async () => {
        if (this.syncing) return;
        this.syncing = true;
        try {
          const cart = await this.getCart();
          const luxuryItem = cart.items.find((item) => item.variant_id === this.luxuryVariantId);
          const giftItem = cart.items.find((item) => item.variant_id === this.giftVariantId);
          const otherItems = cart.items.filter((item) => item.variant_id !== this.giftVariantId);
          const packagingAttribute = (cart.attributes && cart.attributes['Gift packaging']) || '';

          if (luxuryItem) {
            // Gift packaging is FREE with the hat. Record it as a cart attribute
            // instead of a product line: adding the Gift Packaging product triggers
            // a server-side cart transform that splits the hat into one line per
            // unit (the duplicate-line bug). An attribute has no such side effect.
            let changed = false;
            if (giftItem) {
              await this.removeGift(giftItem.key);
              changed = true;
            }
            if (packagingAttribute !== this.FREE_LABEL) {
              await this.setPackagingAttribute(this.FREE_LABEL);
              changed = true;
            }
            if (changed) {
              this.setBusy(true, 'Adding your complimentary gift packaging…');
              window.location.reload();
            }
            return;
          }

          // No hat in the cart — clear the free marker if it was left behind.
          if (packagingAttribute) {
            await this.setPackagingAttribute('');
            window.location.reload();
            return;
          }

          if (giftItem?.properties?._gift_packaging_source === 'Luxury Sheepskin Sauna Hat') {
            this.setBusy(true, 'Updating gift packaging…');
            await this.clearMessage();
            await this.removeGift(giftItem.key);
            window.location.reload();
            return;
          }

          if (otherItems.length === 0 && giftItem) {
            await this.clearMessage();
            await this.removeGift(giftItem.key);
            window.location.reload();
          }
        } catch (error) {
          this.setBusy(false, error.message);
        } finally {
          this.syncing = false;
        }
      };

      onToggle = async (event) => {
        this.setBusy(true, event.target.checked ? 'Adding gift packaging…' : 'Removing gift packaging…');
        try {
          const cart = await this.getCart();
          const giftItem = cart.items.find((item) => item.variant_id === this.giftVariantId);

          if (event.target.checked && !giftItem) {
            await this.addGift('Customer selected');
          } else if (!event.target.checked && giftItem) {
            await this.clearMessage();
            await this.removeGift(giftItem.key);
          }
          window.location.reload();
        } catch (error) {
          event.target.checked = !event.target.checked;
          this.setBusy(false, error.message);
        }
      };

    }
  );
}

if (!customElements.get('loyly-gift-message')) {
  customElements.define(
    'loyly-gift-message',
    class LoylyGiftMessage extends HTMLElement {
      connectedCallback() {
        this.message = this.querySelector('[data-gift-message]');
        this.counter = this.querySelector('[data-gift-message-count]');
        this.status = this.querySelector('[data-gift-message-status]');
        this.messageTimer = null;

        this.message?.addEventListener('input', this.onMessageInput);
        this.message?.addEventListener('blur', this.saveMessage);
        this.updateCounter();
      }

      disconnectedCallback() {
        this.message?.removeEventListener('input', this.onMessageInput);
        this.message?.removeEventListener('blur', this.saveMessage);
        window.clearTimeout(this.messageTimer);
      }

      request = async (url, body) => {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error('Gift card message could not be saved.');
        return response.json();
      };

      onMessageInput = () => {
        this.updateCounter();
        window.clearTimeout(this.messageTimer);
        this.messageTimer = window.setTimeout(this.saveMessage, 650);
      };

      updateCounter = () => {
        if (this.counter && this.message) this.counter.textContent = this.message.value.length;
      };

      saveMessage = async () => {
        if (!this.message) return;
        window.clearTimeout(this.messageTimer);
        try {
          await this.request(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
            attributes: { 'Gift message': this.message.value.trim() },
          });
          if (this.status) this.status.textContent = this.message.value.trim() ? 'Card message saved.' : '';
        } catch (error) {
          if (this.status) this.status.textContent = error.message;
        }
      };
    }
  );
}
